import { Component } from '@theme/component';

/**
 * Steps: a set of steps where one is open at a time. On desktop the step
 * buttons sit in a row above the open panel; on mobile they stack as an
 * accordion (see blocks/sb-tabs.liquid for the layout).
 *
 * Each step is a disclosure button (aria-expanded) controlling its panel.
 * Left and right arrow keys, Home, and End move focus between the step
 * buttons. In the theme editor, selecting a step's block opens it.
 *
 * @typedef {object} StepsRefs
 * @property {HTMLButtonElement[]} buttons - The step buttons
 * @property {HTMLElement[]} panels - The step panels, one per button
 *
 * @extends {Component<StepsRefs>}
 */
class SbTabsComponent extends Component {
  requiredRefs = ['buttons', 'panels'];

  #controller = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('shopify:block:select', this.#handleBlockSelect, { signal: this.#controller.signal });

    const { buttons } = this.refs;
    const open = buttons.findIndex((button) => button.getAttribute('aria-expanded') === 'true');
    this.open(Math.max(open, 0));
    this.dataset.ready = '';
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  /**
   * Opens the clicked step.
   * @param {MouseEvent} event
   */
  openStep(event) {
    this.open(this.refs.buttons.indexOf(/** @type {HTMLButtonElement} */ (event.target)));
  }

  /**
   * Moves focus between step buttons with the arrow, Home, and End keys.
   * @param {KeyboardEvent} event
   */
  handleKeydown(event) {
    const { buttons } = this.refs;
    const current = buttons.indexOf(/** @type {HTMLButtonElement} */ (event.target));
    if (current === -1) return;

    const direction = getComputedStyle(this).direction === 'rtl' ? -1 : 1;

    /** @type {Record<string, number>} */
    const targets = {
      ArrowLeft: current - direction,
      ArrowRight: current + direction,
      Home: 0,
      End: buttons.length - 1,
    };

    if (!(event.key in targets)) return;

    event.preventDefault();
    const next = (/** @type {number} */ (targets[event.key]) + buttons.length) % buttons.length;
    buttons[next]?.focus();
  }

  /**
   * Opens the step at the given index and closes the others.
   * @param {number} index
   */
  open(index) {
    const { buttons, panels } = this.refs;
    if (index < 0 || index >= buttons.length) return;

    for (const [i, button] of buttons.entries()) {
      const isOpen = i === index;
      button.setAttribute('aria-expanded', String(isOpen));
      panels[i]?.toggleAttribute('hidden', !isOpen);
    }
  }

  /**
   * Opens a step when its block is selected in the theme editor.
   * @param {Event} event
   */
  #handleBlockSelect = (event) => {
    if (!(event.target instanceof Element)) return;

    const panel = event.target.closest('.sb-tab__panel');
    const index = panel instanceof HTMLElement ? this.refs.panels.indexOf(panel) : -1;
    if (index !== -1) this.open(index);
  };
}

if (!customElements.get('sb-tabs-component')) {
  customElements.define('sb-tabs-component', SbTabsComponent);
}
